import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1654515989563-0a3bf0f6b616?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-stone-300">{label}</Label>
    <Input id={id} data-testid={`auth-${id}-input`} className="h-12 bg-stone-900 border-stone-700 focus-visible:ring-amber-500" {...props} />
  </div>
);

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else if (mode === "forgot") { await api.post("/auth/forgot-password", { email: form.email }); setSent(true); }
      else await register(form);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] min-h-[calc(100vh-72px)]">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Strada con auto parcheggiate" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-stone-950/60" />
        <div className="absolute bottom-12 left-12 right-12 rise">
          <p className="font-mono-x text-xs uppercase tracking-widest text-amber-400">Comunità di quartiere</p>
          <h2 className="font-display text-5xl font-extrabold leading-[1.05] mt-3 text-amber-50">
            Chi parte, avvisa.<br />Chi arriva, parcheggia.
          </h2>
          <p className="mt-4 text-stone-300 max-w-md">Segnala quando stai per lasciare il tuo posto auto, fino a 60 minuti prima. In forma anonima.</p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-center px-5 sm:px-12 py-8 lg:py-12">
        <div data-testid="auth-mobile-hero" className="lg:hidden relative overflow-hidden rounded-2xl mb-8 rise">
          <img src={HERO} alt="Strada con auto parcheggiate" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-stone-950/70" />
          <div className="relative p-6">
            <p className="font-mono-x text-xs uppercase tracking-widest text-amber-400">Comunità di quartiere</p>
            <h2 className="font-display text-3xl font-extrabold leading-[1.05] mt-2 text-amber-50">
              Chi parte, avvisa.<br />Chi arriva, parcheggia.
            </h2>
            <p className="mt-3 text-sm text-stone-300">Segnala quando stai per lasciare il tuo posto auto, fino a 60 minuti prima. In forma anonima.</p>
          </div>
        </div>
        <form onSubmit={submit} className="w-full max-w-md space-y-5 rise" data-testid="auth-form">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              {{ login: "Bentornato", register: "Unisciti alla comunità", forgot: "Password dimenticata" }[mode]}
            </h2>
            <p className="text-stone-400 mt-2 text-sm">
              {{
                login: "Accedi per vedere i parcheggi che si liberano.",
                register: "Crea un account: gli altri utenti non vedranno mai i tuoi dati.",
                forgot: "Inserisci la tua email: ti invieremo un link per scegliere una nuova password.",
              }[mode]}
            </p>
          </div>
          {mode === "register" && <Field id="name" label="Nome" value={form.name} onChange={set("name")} required />}
          <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} required />
          {mode !== "forgot" && <Field id="password" label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} />}
          {mode === "login" && (
            <button type="button" data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setError(""); setSent(false); }} className="text-sm text-stone-400 hover:text-amber-300 transition-colors">
              Password dimenticata?
            </button>
          )}
          {sent && (
            <p data-testid="auth-forgot-sent" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
              Se l'email è registrata, riceverai a breve un link per reimpostare la password. Controlla anche lo spam.
            </p>
          )}
          {mode === "register" && <Field id="phone" label="Telefono (facoltativo, visibile solo all'admin)" value={form.phone} onChange={set("phone")} />}
          {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
          <button
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#E0533C] hover:bg-[#C8432D] font-semibold text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {{ login: "Accedi", register: "Registrati", forgot: "Invia link" }[mode]}
          </button>
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setSent(false); }}
            className="w-full text-sm text-amber-400 hover:text-amber-300 transition-colors"
          >
            {mode === "login" ? "Non hai un account? Registrati" : mode === "forgot" ? "Torna all'accesso" : "Hai già un account? Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}
